import React from "react";
import { ProductFormFieldsProps } from "./types";

/**
 * Reusable form fields component for product configuration
 */
const ProductFormFields: React.FC<ProductFormFieldsProps> = ({
  register,
  errors,
  showCustomerProductName = true,
}) => {
  // Handle numeric input - only allow numbers, decimal point, and backspace/delete
  const handleNumericInput = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const allowedKeys = [
      "Backspace",
      "Delete",
      "Tab",
      "Escape",
      "Enter",
      "ArrowLeft",
      "ArrowRight",
      "ArrowUp",
      "ArrowDown",
      "Home",
      "End",
    ];

    // Allow control keys
    if (allowedKeys.includes(e.key) || e.ctrlKey || e.metaKey) {
      return;
    }

    // Allow numbers and decimal point
    if (!/[0-9.]/.test(e.key)) {
      e.preventDefault();
    }

    // Prevent multiple decimal points
    const currentValue = (e.target as HTMLInputElement).value;
    if (e.key === "." && currentValue.includes(".")) {
      e.preventDefault();
    }
  };

  // Handle integer input - only allow whole numbers
  const handleIntegerInput = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const allowedKeys = [
      "Backspace",
      "Delete",
      "Tab",
      "Escape",
      "Enter",
      "ArrowLeft",
      "ArrowRight",
      "ArrowUp",
      "ArrowDown",
      "Home",
      "End",
    ];

    // Allow control keys
    if (allowedKeys.includes(e.key) || e.ctrlKey || e.metaKey) {
      return;
    }

    // Only allow numbers
    if (!/[0-9]/.test(e.key)) {
      e.preventDefault();
    }
  };

  return (
    <div className="space-y-4">
      {/* Customer Product Name - Only for parent products */}
      {showCustomerProductName && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Customer product name
          </label>
          <div className="relative">
            <input
              {...register("customerProductName", {
                minLength: {
                  value: 5,
                  message:
                    "Customer product name must be at least 5 characters",
                },
              })}
              placeholder="Customer product name"
              className={`w-full px-3 py-2 border rounded-md focus:outline-none ${
                errors.customerProductName
                  ? "border-red-500"
                  : "border-gray-300"
              }`}
            />
            {errors.customerProductName && (
              <p className="text-red-500 text-xs mt-1">
                {errors.customerProductName.message}
              </p>
            )}
            <div className="absolute right-3 top-2.5">
              <div className="w-5 h-5 bg-gray-200 rounded-full flex items-center justify-center">
                <span className="text-xs text-gray-600">?</span>
              </div>
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            This name will appear in the customer's catalog.
          </p>
        </div>
      )}

      {/* Price Field */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Price <span className="text-primary-blue">*</span>
        </label>
        <div className="relative">
          <input
            {...register("price", {
              required: "Price is required",
              min: { value: 0, message: "Price must be positive" },
            })}
            type="text"
            onKeyDown={handleNumericInput}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none"
          />
          <div className="absolute right-3 top-2.5">
            <div className="w-5 h-5 bg-gray-200 rounded-full flex items-center justify-center">
              <span className="text-xs text-gray-600">?</span>
            </div>
          </div>
        </div>
        <p className="text-xs text-gray-500 mt-1">
          This is the recommended price after calculating the user account
          discount.
        </p>
      </div>

      {/* Quantity Fields */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Minimum Quantity <span className="text-primary-blue">*</span>
          </label>
          <input
            {...register("minQuantity", {
              required: "Minimum quantity is required",
              min: {
                value: 1,
                message: "Minimum quantity must be at least 1",
              },
            })}
            type="text"
            placeholder="Minimum Quantity"
            onKeyDown={handleIntegerInput}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Maximum Quantity <span className="text-primary-blue">*</span>
          </label>
          <input
            {...register("maxQuantity", {
              required: "Maximum quantity is required",
              min: {
                value: 1,
                message: "Maximum quantity must be at least 1",
              },
              max: {
                value: 999,
                message: "Maximum quantity cannot exceed 999",
              },
            })}
            type="text"
            placeholder="Maximum Quantity"
            onKeyDown={handleIntegerInput}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none"
          />
          {errors.maxQuantity?.message ===
            "Maximum quantity cannot exceed 999" && (
            <p className="text-red-500 text-xs">
              {errors.maxQuantity?.message}
            </p>
          )}
        </div>
      </div>

      {/* Customer Product ID */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Customer - Product ID
        </label>
        <div className="relative">
          <input
            {...register("customerProductId", {
              pattern: {
                value: /^[a-zA-Z0-9]+$/,
                message:
                  "Customer Product ID can only contain letters and numbers",
              },
              minLength: {
                value: 2,
                message: "Customer Product ID must be at least 2 characters",
              },
              maxLength: {
                value: 50,
                message: "Customer Product ID must be less than 50 characters",
              },
            })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none"
          />
          {errors.customerProductId && (
            <p className="text-red-500 text-xs mt-1">
              {errors.customerProductId.message}
            </p>
          )}
          <div className="absolute right-3 top-2.5">
            <div className="w-5 h-5 bg-gray-200 rounded-full flex items-center justify-center">
              <span className="text-xs text-gray-600">?</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductFormFields;
